import React, { useState, useEffect, useCallback } from 'react';
import { format as formatDate } from 'date-fns';
import { useSearchParams } from 'react-router-dom';
import {
  Checkbox,
  Container,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Button,
  Typography,
  Box,
  Alert,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import SecurityOutlinedIcon from '@mui/icons-material/SecurityOutlined';
import AccountBalanceOutlinedIcon from '@mui/icons-material/AccountBalanceOutlined';
import AccessibilityNewOutlinedIcon from '@mui/icons-material/AccessibilityNewOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloseIcon from '@mui/icons-material/Close';
import api from '../services/api';
import {
  OrderCategory,
  OrderSubcategory,
  OrderProduct,
  OrderSubmit,
  OrderTally,
  OrderHardware,
  OrderAddon,
  CouponValidateResponse,
} from '../types/order';
import ProductSelection from '../components/order/ProductSelection';
import EquipmentSelection from '../components/order/EquipmentSelection';
import VoiceServiceSelection from '../components/order/VoiceServiceSelection';
import CustomerDetails from '../components/order/CustomerDetails';
import PaymentDetails, { PaymentDraft } from '../components/order/PaymentDetails';
import OrderSummary from '../components/order/OrderSummary';
import SignatureCapture from '../components/order/SignatureCapture';
import OrderTallyComponent from '../components/order/OrderTallyComponent';
import OrderPageHeader from '../components/order/OrderPageHeader';
import OrderConfirmation from '../components/order/OrderConfirmation';
import CaptchaWidget from '../components/order/CaptchaWidget';
import { ensureGooglePlacesLoaded } from '../utils/googlePlacesLoader';

const NAVY = '#0d1f3c';
const BRAND_BLUE = '#3b9eff';

const steps = [
  'Select Plan',
  'Equipment',
  'Voice Service',
  'Customer Details',
  'Payment',
  'Review & Submit',
];

// ---------------------------------------------------------------------------
// Legal section helper components
// ---------------------------------------------------------------------------

function LegalSectionCard({ icon, title, children }: { icon: React.ReactNode; title: string; children: React.ReactNode }) {
  return (
    <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3, overflow: 'hidden', mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 3, py: 2, borderBottom: '1px solid', borderColor: 'divider', bgcolor: '#fafbfc' }}>
        <Box sx={{ color: NAVY, display: 'flex' }}>{icon}</Box>
        <Typography variant="subtitle1" fontWeight={700} color={NAVY}>{title}</Typography>
      </Box>
      <Box sx={{ p: 3 }}>{children}</Box>
    </Paper>
  );
}

function LegalBox({ children }: { children: React.ReactNode }) {
  return <Box sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8f9fb', border: '1px solid', borderColor: 'divider' }}>{children}</Box>;
}

function LegalPara({ children }: { children: React.ReactNode }) {
  return <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75, mb: 1.5 }}>{children}</Typography>;
}

const CREDIT_CHECK_POINTS = [
  'Understand that the Privacy Act 1988 (Cth) allows us to give a credit reporting agency certain personal information about you;',
  'The information we disclose to a credit reporting agency includes information which will allow you to be identified, the fact that you have applied for credit and the amount, that we are a current credit provider to you, repayments that are more than 60 days overdue and for which debt collection action has started, information that in our opinion you have acted fraudulently or shown an intention not to comply with your credit obligations, and cheques drawn by you for $100 or more which have been dishonoured more than once;',
  'Agree to our obtaining from a credit reporting agency a credit report containing information about your personal credit worthiness for purposes of assessing your application and assisting in collecting overdue payments, and to our obtaining information about your commercial activities or commercial credit worthiness from any business which provides information about the commercial credit worthiness of persons, your accountant or any other supplier to you;',
  'Agree to our giving to and obtaining from any credit provider named in your application or in a credit report on you issued by a credit reporting agency, information about your credit arrangements for the purpose of assessing your application, notifying a default by you, allowing another credit provider to ascertain the status of your arrangements with us where you are in default with one or more other credit providers and generally assessing your credit worthiness; and',
  'Understand the information exchanged can include any information about your business, personal and/or commercial credit worthiness, credit history or credit capacity which the Privacy Act allows credit providers to give to or receive from each other.',
];

function OrderPage() {
  const [searchParams] = useSearchParams();
  
  // Deep link parameters
  const categorySlug = searchParams.get('category');
  const subcategorySlug = searchParams.get('subcategory');
  /** Plan deeplink: `product=` (canonical) or `slug=` (common in shared links) */
  const productSlug =
    (searchParams.get('product') || searchParams.get('slug') || '').trim() || null;
  /** supaBuildingKey() of the building carried over from the address-check widget deep link */
  const buildingKey = searchParams.get('building')?.trim() || undefined;
  
  // State
  const [activeStep, setActiveStep] = useState(0);
  /** Initial catalog fetch only — do not reuse for submit (would hide the review step). */
  const [catalogLoading, setCatalogLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [categories, setCategories] = useState<OrderCategory[]>([]);
  const [subcategories, setSubcategories] = useState<OrderSubcategory[]>([]);
  const [products, setProducts] = useState<OrderProduct[]>([]);
  const [hardware, setHardware] = useState<any[]>([]);
  const [addOns, setAddOns] = useState<OrderAddon[]>([]);
  const [voicePlans, setVoicePlans] = useState<OrderProduct[]>([]);
  const [voiceProductFieldsConfig, setVoiceProductFieldsConfig] = useState<{ field_key: string; display_order: number; label_override?: string; is_visible: boolean }[]>([]);

  // Order data (service address captured above plan carousel on step 0)
  const [serviceAddress, setServiceAddress] = useState<string>('');
  const [requestedActivationDate, setRequestedActivationDate] = useState<string>(() =>
    formatDate(new Date(), 'yyyy-MM-dd')
  );
  const [selectedCategory, setSelectedCategory] = useState<OrderCategory | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<OrderSubcategory | null>(null);
  const [selectedInternetPlan, setSelectedInternetPlan] = useState<OrderProduct | null>(null);
  const [selectedHardware, setSelectedHardware] = useState<OrderHardware[]>([]);
  const [selectedAddOns, setSelectedAddOns] = useState<OrderAddon[]>([]);
  const [contractType, setContractType] = useState<string>('no_contract');
  const [voiceServiceSelected, setVoiceServiceSelected] = useState(false);
  const [selectedVoicePlan, setSelectedVoicePlan] = useState<OrderProduct | null>(null);
  const [voiceChannels, setVoiceChannels] = useState<number>(1);
  const [portingNumber, setPortingNumber] = useState<string>('');
  const [portingDocument, setPortingDocument] = useState<string>('');
  const [customerType, setCustomerType] = useState<'residential' | 'business'>('residential');
  const [customerData, setCustomerData] = useState<any>(null);
  const [paymentData, setPaymentData] = useState<any>(null);
  const [paymentDraft, setPaymentDraft] = useState<PaymentDraft | null>(null);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [orderResponse, setOrderResponse] = useState<any>(null);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [customerSignature, setCustomerSignature] = useState<string | null>(null);
  const [customerDetailsAttempted, setCustomerDetailsAttempted] = useState(false);
  const [paymentDetailsAttempted, setPaymentDetailsAttempted] = useState(false);
  const [directMarketingOptOut, setDirectMarketingOptOut] = useState(false);
  const [pdfModal, setPdfModal] = useState<{ label: string; src: string } | null>(null);
  
  // Coupon
  const [appliedCoupon, setAppliedCoupon] = useState<CouponValidateResponse | null>(null);

  // Tally
  const [tally, setTally] = useState<OrderTally>({
    monthly_recurring: 0,
    plan_monthly: 0,
    addon_monthly: 0,
    voice_monthly: 0,
    setup_fees: 0,
    equipment_total: 0,
    delivery_fee: 0,
    total_one_time: 0,
    total_monthly: 0,
  });

  const calculateTally = useCallback(() => {
    let monthly = 0;
    let planMonthly = 0;
    let addonMonthly = 0;
    let voiceMonthly = 0;
    let setup = 0;
    let equipment = 0;
    let delivery = 0;
    const num = (v: unknown): number => (v != null && v !== '' ? Number(v) : 0) || 0;

    if (selectedInternetPlan) {
      planMonthly += num(selectedInternetPlan.monthly_price);
      setup += num(selectedInternetPlan.setup_price);
      setup += num(selectedInternetPlan.one_time_price);
    }
    monthly += planMonthly;

    selectedAddOns.forEach((item) => {
      addonMonthly += num(item.monthly_price);
      setup += num(item.setup_price);
      setup += num(item.one_time_price);
    });
    monthly += addonMonthly;

    let maxDelivery = 0;
    selectedHardware.forEach((item) => {
      let eff: number;
      if (contractType === 'no_contract' && item.price_no_contract != null) {
        eff = num(item.price_no_contract);
      } else if (contractType === '12_month' && item.price_12_month != null) {
        eff = num(item.price_12_month);
      } else if (contractType === '24_month' && item.price_24_month != null) {
        eff = num(item.price_24_month);
      } else {
        eff = num(item.effective_price ?? item.price ?? 0);
      }
      equipment += eff;
      const df = num(item.delivery_fee);
      if (df > maxDelivery) maxDelivery = df;
    });
    delivery = maxDelivery || (selectedHardware.length > 0 ? 20 : 0);

    if (selectedVoicePlan && voiceServiceSelected) {
      voiceMonthly += num(selectedVoicePlan.monthly_price) * voiceChannels;
      setup += num(selectedVoicePlan.setup_price);
    }
    monthly += voiceMonthly;

    const totalOneTime = setup + equipment + delivery;
    const totalMonthly = monthly;

    setTally({
      monthly_recurring: monthly,
      plan_monthly: planMonthly,
      addon_monthly: addonMonthly,
      voice_monthly: voiceMonthly,
      setup_fees: setup,
      equipment_total: equipment,
      delivery_fee: delivery,
      total_one_time: totalOneTime,
      total_monthly: totalMonthly,
    });
  }, [selectedInternetPlan, selectedHardware, selectedAddOns, selectedVoicePlan, voiceChannels, voiceServiceSelected, contractType]);

  // Load initial data
  useEffect(() => {
    loadInitialData();
  }, []);

  // Start Places script in parallel with order catalog fetch so address search is ready sooner on step 1
  useEffect(() => {
    ensureGooglePlacesLoaded();
  }, []);
  
  // Apply deep link selections
  useEffect(() => {
    if (categories.length > 0 && categorySlug) {
      const cat = categories.find(c => c.slug === categorySlug);
      if (cat) {
        setSelectedCategory(cat);
        // Load subcategories for this category
        loadSubcategories(cat.id);
      }
    }
  }, [categories, categorySlug]);
  
  useEffect(() => {
    if (subcategories.length > 0 && subcategorySlug && selectedCategory) {
      const subcat = subcategories.find(s => s.slug === subcategorySlug);
      if (subcat) {
        setSelectedSubcategory(subcat);
        loadProductsBySlug(selectedCategory.slug, subcat.slug);
        loadHardware(selectedCategory.slug, subcat.slug);
        loadAddOns(selectedCategory.slug, subcat.slug);
      }
    }
  }, [subcategories, subcategorySlug, selectedCategory]);
  
  useEffect(() => {
    if (products.length > 0 && productSlug) {
      const want = productSlug.toLowerCase();
      const product = products.find(
        (p) =>
          (p.slug || '').toLowerCase() === want && p.product_type === 'internet_plan'
      );
      if (product) {
        setSelectedInternetPlan(product);
        // Stay on product step so user can enter service address etc before progressing
      }
    }
  }, [products, productSlug]);
  
  // Update tally when selections change
  useEffect(() => {
    calculateTally();
  }, [calculateTally]);
  
  const loadInitialData = async () => {
    try {
      setCatalogLoading(true);
      const cats = await api.getOrderCategories();
      setCategories(cats);

      // Internet plans are loaded per category/subcategory once one is selected
      // (deep link or manual) via loadProductsBySlug — no unfiltered preload here,
      // since it used to race that call and could clobber it with every plan
      // in the catalog if the unfiltered fetch happened to resolve second.
      setHardware([]);
      setAddOns([]);

      // Voice: load plans for Voice - Residential subcategory (residential internet order flow)
      const voiceCategorySlug = 'voice';
      const voiceSubcategorySlug = 'voice-residential';
      try {
        const vPlans = await api.getOrderProducts({
          category: voiceCategorySlug,
          subcategory: voiceSubcategorySlug,
          type: 'voice_plan',
        });
        const sorted = [...vPlans].sort(
          (a, b) =>
            Number(a.display_order ?? 0) - Number(b.display_order ?? 0) ||
            (a.name || '').localeCompare(b.name || '')
        );
        setVoicePlans(sorted);
      } catch {
        // Fallback: use all voice_plan products if slug filter fails
        const allProducts = await api.getOrderProducts();
        const fallback = allProducts.filter((p: OrderProduct) => p.product_type === 'voice_plan');
        const sorted = [...fallback].sort(
          (a, b) =>
            Number(a.display_order ?? 0) - Number(b.display_order ?? 0) ||
            (a.name || '').localeCompare(b.name || '')
        );
        setVoicePlans(sorted);
      }
      try {
        const vFields = await api.getOrderProductFields({
          category: voiceCategorySlug,
          subcategory: voiceSubcategorySlug,
        });
        setVoiceProductFieldsConfig(
          vFields.filter((f: { is_visible: boolean }) => f.is_visible).sort((a: { display_order: number }, b: { display_order: number }) => a.display_order - b.display_order)
        );
      } catch {
        setVoiceProductFieldsConfig([]);
      }

      setCatalogLoading(false);
    } catch (err: any) {
      setError(err.message || 'Failed to load order data');
      setCatalogLoading(false);
    }
  };
  
  const loadSubcategories = async (categoryId: string) => {
    try {
      const cats = await api.getOrderCategories();
      const cat = cats.find(c => c.id === categoryId);
      if (cat) {
        setSubcategories(cat.subcategories || []);
        // Keep dropdown + deeplink in sync with nested subcategories from API
        setSelectedCategory((prev) => (prev && prev.id === categoryId ? cat : prev));
      }
    } catch (err: any) {
      console.error('Failed to load subcategories', err);
    }
  };
  
  /** Load internet plans by category and subcategory slug (e.g. nbn, supa). Sorted by display_order then name. */
  const loadProductsBySlug = async (categorySlug: string, subcategorySlug: string) => {
    try {
      const prods = await api.getOrderProducts({
        category: categorySlug,
        subcategory: subcategorySlug,
        type: 'internet_plan',
      });
      const sorted = [...prods].sort(
        (a, b) =>
          Number(a.display_order ?? 0) - Number(b.display_order ?? 0) ||
          (a.name || '').localeCompare(b.name || '')
      );
      setProducts(sorted);
    } catch (err: any) {
      console.error('Failed to load products', err);
    }
  };

  /** Load hardware for equipment step by category and subcategory slug. */
  const loadHardware = async (categorySlug: string, subcategorySlug: string) => {
    try {
      const hw = await api.getOrderHardware({ category: categorySlug, subcategory: subcategorySlug });
      const sorted = [...hw].sort(
        (a, b) =>
          Number(a.display_order ?? 0) - Number(b.display_order ?? 0) ||
          (a.name || '').localeCompare(b.name || '')
      );
      setHardware(sorted);
    } catch (err: any) {
      console.error('Failed to load hardware', err);
      setHardware([]);
    }
  };

  /** Load equipment-step add-ons (e.g. Static IP) by category and subcategory slug. */
  const loadAddOns = async (categorySlug: string, subcategorySlug: string) => {
    try {
      const addonsList = await api.getOrderAddons({
        category: categorySlug,
        subcategory: subcategorySlug,
      });
      const sorted = [...addonsList].sort(
        (a, b) =>
          Number(a.display_order ?? 0) - Number(b.display_order ?? 0) ||
          (a.name || '').localeCompare(b.name || '')
      );
      setAddOns(sorted);
    } catch (err: any) {
      console.error('Failed to load add-ons', err);
      setAddOns([]);
    }
  };

  /** Called when user clicks "Select plan" on a plan card. Updates selection (and tally) and clears any step error. */
  const handlePlanSelect = (plan: OrderProduct) => {
    setError(null);
    setSelectedInternetPlan(plan);
  };

  const handleNext = () => {
    // Validate current step before proceeding
    if (activeStep === 0 && !selectedInternetPlan) {
      setError('Please select an internet plan');
      return;
    }
    if (activeStep === 1 && selectedInternetPlan && selectedHardware.length === 0) {
      // Equipment is optional, allow proceeding
    }
    if (activeStep === 2 && voiceServiceSelected && !selectedVoicePlan) {
      setError('Please select a voice plan');
      return;
    }
    if (activeStep === 3) {
      const d = customerData || {};
      const missing: string[] = [];
      if (customerType === 'business') {
        if (!d.organisationName?.trim()) missing.push('Organisation Name');
        if (!d.abnAcn?.trim()) missing.push('ABN/ACN');
      }
      if (!d.firstName?.trim()) missing.push('First Name');
      if (!d.lastName?.trim()) missing.push('Last Name');
      if (!d.email?.trim()) missing.push('Email');
      else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(d.email)) missing.push('Email (invalid format)');
      if (!d.mobileNumber?.trim()) missing.push('Mobile Number');
      if (!d.dateOfBirth) missing.push('Date of Birth');
      if (!d.serviceAddress?.trim()) missing.push('Address');

      if (missing.length > 0) {
        setCustomerDetailsAttempted(true);
        setError(`Please complete the following required fields: ${missing.join(', ')}`);
        return;
      }
      setCustomerDetailsAttempted(false);
    }
    if (activeStep === 4) {
      const d = paymentData || {};
      const missing: string[] = [];
      if (d.payment_method === 'credit_card' || !d.payment_method) {
        const cc = d.credit_card || {};
        if (!cc.card_number?.trim()) missing.push('Card Number');
        if (!cc.expiry_month) missing.push('Expiry Month');
        if (!cc.expiry_year) missing.push('Expiry Year');
        if (!cc.cvv?.trim()) missing.push('CVV');
        if (!cc.name_on_card?.trim()) missing.push('Name on Card');
      } else {
        const dd = d.direct_debit || {};
        if ((dd.bsb || '').replace(/\D/g, '').length !== 6) missing.push('BSB');
        if (!dd.account_number?.trim()) missing.push('Account Number');
        if (!dd.account_name?.trim()) missing.push('Account Name');
      }
      if (!d.terms_accepted) missing.push('Ezidebit terms and conditions');
      if (missing.length > 0) {
        setPaymentDetailsAttempted(true);
        setError(`Please complete the following required fields: ${missing.join(', ')}`);
        return;
      }
      setPaymentDetailsAttempted(false);
    }
    
    setError(null);
    setActiveStep(prev => Math.min(prev + 1, steps.length - 1));
  };
  
  const handleBack = () => {
    setError(null);
    setActiveStep((prev) => {
      if (prev === 3) setCustomerDetailsAttempted(false);
      if (prev === 4) setPaymentDetailsAttempted(false);
      return Math.max(prev - 1, 0);
    });
  };
  
  const handleSubmit = async () => {
    try {
      setError(null);
      
      if (!captchaToken) {
        setError('Please complete CAPTCHA verification');
        return;
      }
      if (
        !customerSignature ||
        !customerSignature.startsWith('data:image/png;base64,')
      ) {
        setError('Please sign in the signature box to confirm your order');
        return;
      }
      
      setSubmitting(true);
      
      // Build order submission
      const order: OrderSubmit = {
        customer_type: customerType,
        first_name: customerData.firstName,
        last_name: customerData.lastName,
        email: customerData.email,
        mobile_number: customerData.mobileNumber,
        date_of_birth: customerData.dateOfBirth ? formatDate(customerData.dateOfBirth, 'yyyy-MM-dd') : undefined,
        service_address: customerData.serviceAddress,
        billing_address: customerData.billingAddress || customerData.serviceAddress,
        requested_activation_date: selectedCategory?.slug === 'internet' ? requestedActivationDate : undefined,
        organisation_name: customerType === 'business' ? customerData.organisationName : undefined,
        abn_acn: customerType === 'business' ? customerData.abnAcn : undefined,
        internet_plan_id: selectedInternetPlan!.id,
        equipment_ids: [],
        hardware_ids: selectedHardware.map(h => h.id),
        addon_ids: selectedAddOns.map(a => a.id),
        contract_type: contractType,
        voice_service_selected: voiceServiceSelected,
        voice_plan_id: voiceServiceSelected ? selectedVoicePlan?.id : undefined,
        voice_channels: voiceServiceSelected ? voiceChannels : undefined,
        porting_number: voiceServiceSelected && portingNumber ? portingNumber : undefined,
        porting_document: voiceServiceSelected && portingDocument ? portingDocument : undefined,
        payment: paymentData,
        customer_signature: customerSignature,
        captcha_token: captchaToken,
        coupon_code: appliedCoupon?.code,
      };
      
      const response = await api.submitOrder(order);
      setOrderResponse(response);
      setOrderSubmitted(true);
      setActiveStep(steps.length); // Move to confirmation
      
    } catch (err: any) {
      const detail = err.response?.data?.detail;
      if (typeof detail === 'string') {
        setError(detail);
      } else if (Array.isArray(detail)) {
        setError(detail.map((e: { msg?: string }) => e.msg).filter(Boolean).join('; ') || 'Please check your order details and try again.');
      } else {
        setError(err.message || 'Failed to submit order');
      }
    } finally {
      setSubmitting(false);
    }
  };
  
  const handleCaptchaVerify = useCallback((token: string) => {
    setCaptchaToken(token);
  }, []);

  const handleCaptchaError = useCallback(() => {
    setError('CAPTCHA verification failed. Please try again.');
  }, []);

  if (orderSubmitted && orderResponse) {
    return <OrderConfirmation order={orderResponse} />;
  }
  
  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <OrderPageHeader />
      <Container maxWidth="lg" sx={{ py: 4, flex: 1 }}>
        <Grid container spacing={3} alignItems="stretch">
          {/* Main stepper content */}
          <Grid item xs={12} md={8}>
            <Paper elevation={3} sx={{ p: 4 }}>
              <Typography variant="h4" gutterBottom align="center">
                Order Internet Services
              </Typography>

              <Stepper activeStep={activeStep} sx={{ mt: 4, mb: 4 }}>
                {steps.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>

              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              {catalogLoading && (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              )}

              {!catalogLoading && (
                <Box sx={{ minHeight: '400px' }}>
                  {activeStep === 0 && (
                    <ProductSelection
                      categories={categories}
                      selectedCategory={selectedCategory}
                      selectedSubcategory={selectedSubcategory}
                      products={products}
                      selectedPlan={selectedInternetPlan}
                      serviceAddress={serviceAddress}
                      onServiceAddressChange={setServiceAddress}
                      requestedActivationDate={requestedActivationDate}
                      onRequestedActivationDateChange={setRequestedActivationDate}
                      initialBuildingKey={buildingKey}
                      onCategoryChange={setSelectedCategory}
                      onSubcategoryChange={(subcat) => {
                        setSelectedSubcategory(subcat);
                        setSelectedHardware([]);
                        setSelectedAddOns([]);
                        if (selectedCategory) {
                          loadProductsBySlug(selectedCategory.slug, subcat.slug);
                          loadHardware(selectedCategory.slug, subcat.slug);
                          loadAddOns(selectedCategory.slug, subcat.slug);
                        }
                      }}
                      onPlanSelect={handlePlanSelect}
                    />
                  )}

                  {activeStep === 1 && selectedInternetPlan && (
                    <EquipmentSelection
                      hardware={hardware}
                      selectedHardware={selectedHardware}
                      onHardwareChange={setSelectedHardware}
                      addOns={addOns}
                      selectedAddOns={selectedAddOns}
                      onAddOnsChange={setSelectedAddOns}
                      contractType={contractType}
                      onContractTypeChange={setContractType}
                    />
                  )}

                  {activeStep === 2 && (
                    <VoiceServiceSelection
                      customerType={customerType}
                      voicePlans={voicePlans}
                      selectedPlan={selectedVoicePlan}
                      voiceChannels={voiceChannels}
                      portingNumber={portingNumber}
                      portingDocument={portingDocument}
                      voiceServiceSelected={voiceServiceSelected}
                      onServiceToggle={setVoiceServiceSelected}
                      onPlanSelect={setSelectedVoicePlan}
                      onChannelsChange={setVoiceChannels}
                      onPortingNumberChange={setPortingNumber}
                      onPortingDocumentChange={setPortingDocument}
                      voiceProductFieldsConfig={voiceProductFieldsConfig.length > 0 ? voiceProductFieldsConfig : undefined}
                    />
                  )}

                  {activeStep === 3 && (
                    <CustomerDetails
                      customerType={customerType}
                      onCustomerTypeChange={setCustomerType}
                      onDataChange={setCustomerData}
                      initialServiceAddress={serviceAddress}
                      showErrors={customerDetailsAttempted}
                      initialData={customerData}
                    />
                  )}

                  {activeStep === 4 && (
                    <PaymentDetails
                      onDataChange={setPaymentData}
                      draft={paymentDraft}
                      onDraftChange={setPaymentDraft}
                      showErrors={paymentDetailsAttempted}
                    />
                  )}

                  {activeStep === 5 && (
                    <>
                      {!customerData && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                          Customer details were not found. Please go back to the Customer details step and continue again.
                        </Alert>
                      )}
                      <OrderSummary
                        internetPlan={selectedInternetPlan}
                        equipment={selectedHardware}
                        addOns={selectedAddOns}
                        voicePlan={selectedVoicePlan}
                        voiceChannels={voiceChannels}
                        voiceServiceSelected={voiceServiceSelected}
                        customerData={customerData}
                        customerType={customerType}
                        serviceAddressStep1={serviceAddress}
                        requestedActivationDate={selectedCategory?.slug === 'internet' ? requestedActivationDate : undefined}
                        paymentMethod={paymentData?.payment_method ?? null}
                        categoryName={selectedCategory?.name}
                        subcategoryName={selectedSubcategory?.name}
                        serviceTypeCodeForLogo={selectedSubcategory?.slug}
                        portingNumber={portingNumber || undefined}
                        hasPortingDocument={Boolean(voiceServiceSelected && portingDocument)}
                        appliedCoupon={appliedCoupon}
                        contractType={contractType}
                      />

                      {/* Application Declaration */}
                      <Box sx={{ mt: 4 }}>
                        <LegalSectionCard icon={<AssignmentOutlinedIcon />} title="Application Declaration">
                          <LegalBox>
                            <LegalPara>
                              The Customer ("You") applies to Sonic Internet &amp; Voice Pty Ltd ABN 61 688 496 924 ("we", "us" or "our")
                              for the supply of the Service(s) listed in the table above. By submitting this Application, you acknowledge
                              and agree that:
                            </LegalPara>
                            <LegalPara>
                              The Service(s) are subject to our Standard Form of Agreement, which is a standard form of agreement for the
                              purposes of Part 23 of the Telecommunications Act 1997 (Cth). By submitting this Application, you confirm
                              that you have been given the opportunity to read, or have read, and agree to be bound by the Standard Form of
                              Agreement (including the applicable Service Description and Critical Information Summary (CIS)), available at{' '}
                              <a href="https://sonicnet.net.au/legal" target="_blank" rel="noreferrer">sonicnet.net.au/legal</a>.
                            </LegalPara>
                            <LegalPara>
                              This Application is accepted by us at the earlier of: (a) the date the Service is activated; or (b) the date
                              we notify you that your Application has been accepted.
                            </LegalPara>
                            <LegalPara>
                              If you agree to a Minimum Term for a Service, early termination during that Minimum Term may incur a
                              Cancellation Charge, calculated as the monthly recurring charge for the terminated Service multiplied by the
                              number of months (or part thereof) remaining in the Minimum Term, unless otherwise stated in the applicable CIS.
                            </LegalPara>
                            <LegalPara>
                              The person submitting this Application warrants that they have full legal capacity to enter into this
                              Agreement, or are duly authorised to execute this Application on behalf of the Customer.
                            </LegalPara>
                            <LegalPara>
                              Any cooling-off rights available to you under the Australian Consumer Law or other applicable legislation are
                              not affected by this Agreement.
                            </LegalPara>
                          </LegalBox>
                        </LegalSectionCard>

                        {/* Privacy & Spam */}
                        <LegalSectionCard icon={<SecurityOutlinedIcon />} title="Privacy &amp; Spam">
                          <LegalBox>
                            <LegalPara>
                              We will collect personal information about you or information about your company, including but not limited to
                              your electronic contact details such as email address and telephone numbers for the purpose of administering
                              the services for which you apply, and as set out in our privacy policy. Please contact us directly to obtain a
                              copy of our privacy policy.
                            </LegalPara>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={directMarketingOptOut}
                                  onChange={(e) => setDirectMarketingOptOut(e.target.checked)}
                                  size="small"
                                  sx={{ color: BRAND_BLUE, '&.Mui-checked': { color: BRAND_BLUE } }}
                                />
                              }
                              label={
                                <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.75 }}>
                                  If you would prefer that we do not use your personal information and/or business information for direct
                                  marketing and/or for sending commercial electronic messages, please check this box.
                                </Typography>
                              }
                              sx={{ alignItems: 'flex-start', m: 0 }}
                            />
                          </LegalBox>
                        </LegalSectionCard>

                        {/* Credit Checks */}
                        <LegalSectionCard icon={<AccountBalanceOutlinedIcon />} title="Credit Checks">
                          <LegalBox>
                            <LegalPara>
                              Prior to accepting your application, you have provided to us all information relevant to our assessment of
                              your credit rating. You consent and acknowledge that you:
                            </LegalPara>
                            <Box component="ul" sx={{ m: 0, pl: 2.5, mb: 0 }}>
                              {CREDIT_CHECK_POINTS.map((point, i) => (
                                <Typography key={i} component="li" variant="body2" color="text.secondary"
                                  sx={{ lineHeight: 1.75, mb: i < CREDIT_CHECK_POINTS.length - 1 ? 1 : 0 }}>
                                  {point}
                                </Typography>
                              ))}
                            </Box>
                          </LegalBox>
                        </LegalSectionCard>

                        {/* National Relay & Interpreting Service */}
                        <LegalSectionCard icon={<AccessibilityNewOutlinedIcon />} title="National Relay &amp; Interpreting Service">
                          <LegalBox>
                            <LegalPara>
                              If you have a disability or do not have English as your first language, the Australian Government provides
                              services to assist in understanding &amp; communicating with us.
                            </LegalPara>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                              <Box>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                  National Relay Service
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color={NAVY}>
                                  <Box component="a" href="tel:133677" sx={{ color: NAVY, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>133 677</Box>
                                </Typography>
                              </Box>
                              <Box>
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                  Translating &amp; Interpreting Service
                                </Typography>
                                <Typography variant="body2" fontWeight={700} color={NAVY}>
                                  <Box component="a" href="tel:131450" sx={{ color: NAVY, textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}>131 450</Box>
                                </Typography>
                              </Box>
                            </Box>
                          </LegalBox>
                        </LegalSectionCard>

                        {/* Terms & Conditions — CIS for each selected product that has one */}
                        {(() => {
                          const cisProducts = [
                            selectedInternetPlan,
                            voiceServiceSelected ? selectedVoicePlan : null,
                          ].filter((p): p is OrderProduct => !!p && !!p.critical_info_summary_path);
                          if (cisProducts.length === 0) return null;
                          const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
                          return (
                            <LegalSectionCard icon={<GavelOutlinedIcon />} title="Terms &amp; Conditions">
                              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                Please read the Critical Information Summary for each service before submitting your order.
                              </Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {cisProducts.map((p) => (
                                  <Paper
                                    key={p.id}
                                    variant="outlined"
                                    onClick={() => setPdfModal({ label: `Critical Information Summary — ${p.name}`, src: `${apiBase}/api/orders/cis/${p.id}` })}
                                    sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { borderColor: BRAND_BLUE, bgcolor: 'rgba(59,158,255,0.04)', '& .doc-arrow': { opacity: 1 } } }}
                                  >
                                    <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(13,31,60,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                      <DescriptionOutlinedIcon sx={{ color: NAVY, fontSize: 22 }} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Typography variant="body2" fontWeight={700} color={NAVY} noWrap>
                                        Critical Information Summary
                                      </Typography>
                                      <Typography variant="caption" color="text.secondary" noWrap>{p.name}</Typography>
                                    </Box>
                                    <OpenInNewIcon className="doc-arrow" sx={{ color: BRAND_BLUE, opacity: 0.4, fontSize: 18, transition: 'opacity 0.15s ease' }} />
                                  </Paper>
                                ))}
                              </Box>
                            </LegalSectionCard>
                          );
                        })()}

                        {/* Standard Form of Agreement */}
                        <LegalSectionCard icon={<DescriptionOutlinedIcon />} title="Standard Form of Agreement">
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Your service is supplied in accordance with our Standard Form of Agreement. Please read this document before submitting your order.
                          </Typography>
                          <Paper
                            variant="outlined"
                            onClick={() => setPdfModal({ label: 'Standard Form of Agreement', src: '/assets/Standard_Form_of_Agreement.pdf' })}
                            sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, borderRadius: 2, cursor: 'pointer', transition: 'all 0.15s ease', '&:hover': { borderColor: BRAND_BLUE, bgcolor: 'rgba(59,158,255,0.04)', '& .doc-arrow': { opacity: 1 } } }}
                          >
                            <Box sx={{ width: 40, height: 40, borderRadius: 1.5, bgcolor: 'rgba(13,31,60,0.07)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                              <DescriptionOutlinedIcon sx={{ color: NAVY, fontSize: 22 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant="body2" fontWeight={700} color={NAVY}>
                                Standard Form of Agreement — Sonic Internet &amp; Voice
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                Telecommunications Act 1997 (Cth) — Part 23
                              </Typography>
                            </Box>
                            <OpenInNewIcon className="doc-arrow" sx={{ color: BRAND_BLUE, opacity: 0.4, fontSize: 18, transition: 'opacity 0.15s ease' }} />
                          </Paper>
                        </LegalSectionCard>
                      </Box>

                      <Box sx={{ mt: 4, maxWidth: 720 }}>
                        <SignatureCapture onChange={setCustomerSignature} disabled={submitting} />
                      </Box>
                      <Box sx={{ mt: 4 }}>
                        <Typography variant="subtitle2" gutterBottom>
                          Security verification
                        </Typography>
                        <CaptchaWidget
                          siteKey={process.env.REACT_APP_TURNSTILE_SITE_KEY || ''}
                          onVerify={handleCaptchaVerify}
                          onError={handleCaptchaError}
                        />
                      </Box>

                      {/* PDF preview modal */}
                      <Dialog open={!!pdfModal} onClose={() => setPdfModal(null)} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: 3, height: '90vh' } }}>
                        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid', borderColor: 'divider', pb: 2 }}>
                          <Typography fontWeight={700} color={NAVY}>{pdfModal?.label}</Typography>
                          <IconButton onClick={() => setPdfModal(null)} size="small"><CloseIcon /></IconButton>
                        </DialogTitle>
                        <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
                          {pdfModal && (
                            <Box component="iframe" src={pdfModal.src} sx={{ flex: 1, width: '100%', border: 'none', minHeight: 0 }} title={pdfModal.label} />
                          )}
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                <Button
                  disabled={activeStep === 0}
                  onClick={handleBack}
                >
                  Back
                </Button>

                {activeStep === steps.length - 1 ? (
                  <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={
                      submitting ||
                      !captchaToken ||
                      !customerSignature ||
                      !customerSignature.startsWith('data:image/png;base64,')
                    }
                  >
                    {submitting ? 'Submitting...' : 'Submit order'}
                  </Button>
                ) : (
                  <Button
                    variant="contained"
                    onClick={handleNext}
                  >
                    Next
                  </Button>
                )}
              </Box>
            </Paper>
          </Grid>

          {/* Order tally — sticky right panel */}
          <Grid item xs={12} md={4}>
            <OrderTallyComponent
              tally={tally}
              internetPlanId={selectedInternetPlan?.id}
              appliedCoupon={appliedCoupon}
              onCouponApplied={setAppliedCoupon}
              contractType={contractType}
            />
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
}

export default OrderPage;

